import { ChangeEvent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "@coworker/locales";
import {
  detectBarcodeFormat,
  extractBasedOnSymbology,
} from "@ingka-group-digital/flexa-scanner";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaFormField } from "@coworker/apprestructured/src/shared/wrappers/FixaFormField/FixaFormField";
import { FixaList } from "@coworker/apprestructured/src/shared/wrappers/FixaList/FixaList";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import FixaTabs, {
  FixaTab,
  FixaTabPanel,
} from "@coworker/apprestructured/src/shared/wrappers/FixaTabs/FixaTabs";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { FixaTextArea } from "@coworker/apprestructured/src/shared/wrappers/FixaTextArea/FixaTextArea";
import {
  ArticleWithQuantity,
  AttachArticlesWithQuantityStepper,
} from "@coworker/apprestructured/src/task-management/components/AttachArticles/AttachArticlesWithQuantityStepper";
import {
  extractArticleNumbersOnly,
  extractProductArticleIdAndQuantity,
  isValidInput,
  MAX_QUANTITY_NUMBER,
  ParseStatus,
  ProductArticleIdAndQuantity,
  validateArticleNumbersOnly,
} from "./AddArticlesFragment.helper";
import styles from "./AddArticlesFragment.module.css";

const MAX_ARTICLES = 150;

export type AddArticlesSelection =
  | { mode: "attach"; articles: ArticleWithQuantity[] }
  | { mode: "insert"; articles: ProductArticleIdAndQuantity[] };

interface AddArticlesFragmentProps {
  onClose: () => void;
  onSubmit: (selection: AddArticlesSelection) => void | Promise<void>;
  preselectedItemNos?: string[];
  maxArticles?: number;
  articleNumberOnly?: boolean;
  isExistingArticlesLoading?: boolean;
  enableDefx?: boolean;
  onMultilinePaste?: (addedCount: number, totalCount: number) => void;
}

export const AddArticlesFragment = ({
  onClose,
  onSubmit,
  preselectedItemNos = [],
  maxArticles,
  articleNumberOnly = false,
  isExistingArticlesLoading = false,
  enableDefx = false,
  onMultilinePaste,
}: AddArticlesFragmentProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("attach");
  const [scannedArticles, setScannedArticles] = useState<ArticleWithQuantity[]>(
    []
  );
  const [inputValue, setInputValue] = useState("");

  const existingArticleNos = useMemo(
    () => new Set(preselectedItemNos),
    [preselectedItemNos]
  );
  const hasNewScannedArticles = useMemo(
    () => scannedArticles.some(({ item }) => !existingArticleNos.has(item.no)),
    [existingArticleNos, scannedArticles]
  );
  const parseStatus = articleNumberOnly
    ? validateArticleNumbersOnly(inputValue)
    : isValidInput(inputValue, { validateArticleNumber: !enableDefx });
  const parsedArticles = useMemo(() => {
    if (!inputValue || parseStatus !== ParseStatus.ParseOk) return [];
    return articleNumberOnly
      ? extractArticleNumbersOnly(inputValue)
      : extractProductArticleIdAndQuantity(inputValue);
  }, [articleNumberOnly, inputValue, parseStatus]);
  const uniqueInsertArticleCount = useMemo(() => {
    const articleIds = new Set(
      parsedArticles.map((article) => article.productArticleId)
    );
    if (articleNumberOnly) {
      return [...articleIds].filter(
        (articleId) => !existingArticleNos.has(articleId)
      ).length;
    }
    return articleIds.size;
  }, [articleNumberOnly, existingArticleNos, parsedArticles]);
  const totalInsertArticleCount = useMemo(
    () =>
      articleNumberOnly
        ? uniqueInsertArticleCount
        : parsedArticles.reduce((sum, article) => sum + article.quantity, 0),
    [articleNumberOnly, parsedArticles, uniqueInsertArticleCount]
  );
  const totalScannedArticleCount = useMemo(
    () => scannedArticles.reduce((sum, article) => sum + article.quantity, 0),
    [scannedArticles]
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = event.target.value;
      const existingArticles = inputValue.match(/\d{8}/g) || [];
      const newArticles = newValue.match(/\d{8}/g) || [];
      const addedCount = newArticles.length - existingArticles.length;
      if (addedCount >= 2) {
        onMultilinePaste?.(addedCount, newArticles.length);
      }

      const lines = newValue.split("\n");
      const lastLineIndex = lines.length - 1;
      const cleanedLines = lines.map((line, index) => {
        if (index === lastLineIndex) return line;
        const trimmed = line.trim();
        const format = detectBarcodeFormat(trimmed);
        if (
          format === "INCOMPLETE" ||
          format === "UNKNOWN" ||
          format === "ARTICLE_NUMBER"
        ) {
          return line;
        }
        try {
          return extractBasedOnSymbology(format, trimmed).articleNumber;
        } catch {
          return line;
        }
      });
      setInputValue(cleanedLines.join("\n"));
    },
    [inputValue, onMultilinePaste]
  );

  const handleSubmit = useCallback(async () => {
    if (activeTab === "attach") {
      await onSubmit({
        mode: "attach",
        articles: scannedArticles.filter(
          ({ item }) => !existingArticleNos.has(item.no)
        ),
      });
      return;
    }

    await onSubmit({ mode: "insert", articles: parsedArticles });
  }, [
    activeTab,
    existingArticleNos,
    onSubmit,
    parsedArticles,
    scannedArticles,
  ]);

  const isSubmitDisabled =
    activeTab === "attach"
      ? isExistingArticlesLoading ||
        !hasNewScannedArticles ||
        (maxArticles !== undefined && totalScannedArticleCount > maxArticles)
      : !inputValue ||
        parseStatus !== ParseStatus.ParseOk ||
        (maxArticles !== undefined && totalInsertArticleCount > maxArticles);

  return (
    <FixaModal
      visible
      handleCloseBtn={onClose}
      focusLockProps={{ locked: false }}
    >
      <FixaSheets
        labelledById="add-multiple-articles-header"
        header={
          <FixaModalHeader
            id="add-multiple-articles-header"
            ariaCloseTxt={t("closeActionString")}
            title={t("addArticlesString")}
          />
        }
        footer={
          <FixaModalFooter>
            <FixaButton
              data-testid="addMultipleArticles:submit"
              type="primary"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
            >
              {t("addArticlesString")}
            </FixaButton>
          </FixaModalFooter>
        }
        fullSize
      >
        <FixaModalBody>
          <FixaTabs
            defaultActiveTab="attach"
            activeTab={activeTab}
            onTabChanged={setActiveTab}
            tabs={[
              <FixaTab
                key="attach"
                text={t("addArticlesString")}
                tabPanelId="attach"
              />,
              <FixaTab
                key="insert"
                text={
                  enableDefx
                    ? t("insertArticlesAndDesignString")
                    : t("insertArticlesString")
                }
                tabPanelId="insert"
              />,
            ]}
            tabPanels={[
              <FixaTabPanel key="attach-panel" tabPanelId="attach">
                <AttachArticlesWithQuantityStepper
                  articles={scannedArticles}
                  setArticles={setScannedArticles}
                  maxArticles={maxArticles ?? MAX_ARTICLES}
                  isActive={activeTab === "attach"}
                  enableMultiSelectSearch
                  preselectedItemNos={preselectedItemNos}
                />
                {maxArticles !== undefined &&
                  totalScannedArticleCount > maxArticles && (
                    <FixaText textStyle="Body.S">
                      {t("youCantAddMoreArticlesString")}
                    </FixaText>
                  )}
              </FixaTabPanel>,
              <FixaTabPanel key="insert-panel" tabPanelId="insert">
                {activeTab === "insert" && (
                  <>
                    {parsedArticles.length > 0 && (
                      <div className={styles["articleCountHeader"]}>
                        <FixaText textStyle="Heading.XS">
                          {uniqueInsertArticleCount === 1
                            ? `1 ${t("articleSelectedString")}`
                            : t("articlesSelectedCountString", {
                                count: uniqueInsertArticleCount,
                              })}{" "}
                          {!articleNumberOnly && (
                            <>
                              ({totalInsertArticleCount}{" "}
                              {t("totalString", {
                                defaultValue: "total",
                              })}
                              )
                            </>
                          )}
                        </FixaText>
                      </div>
                    )}
                    <FixaText textStyle="Label.S">
                      {t("scanArticleRDTString")}
                    </FixaText>
                    <FixaList size="small">
                      {!articleNumberOnly && (
                        <li>
                          {t("scanMultipleTimesToIncreaseQuantityString")}
                        </li>
                      )}
                      <li>{t("oneArticleNumberPerLineString")}</li>
                      {!articleNumberOnly && (
                        <li>{t("addQuantityAfterArticleNumberString")}</li>
                      )}
                      {enableDefx && <li>{t("addRexCodeString")}</li>}
                    </FixaList>
                    <FixaFormField
                      shouldValidate={
                        inputValue.length > 0 &&
                        parseStatus !== ParseStatus.ParseOk
                      }
                      validation={
                        parseStatus === ParseStatus.ParseErrorArticleNumber
                          ? {
                              msg: t("addArticleNumberString"),
                              id: "article-number",
                            }
                          : {
                              msg: t("addQuantityNumberString", {
                                maxQuantity: MAX_QUANTITY_NUMBER,
                              }),
                              id: "quantity-number",
                            }
                      }
                    >
                      <FixaTextArea
                        label=""
                        aria-label={t("scanArticleRDTString")}
                        value={inputValue}
                        rows={12}
                        autoFocus
                        onChange={handleInputChange}
                        data-testid="addMultipleArticles:input"
                        onClick={(event) => event.stopPropagation()}
                      />
                    </FixaFormField>
                    {maxArticles !== undefined &&
                      totalInsertArticleCount > maxArticles && (
                        <FixaText textStyle="Body.S">
                          {t("youCantAddMoreArticlesString")}
                        </FixaText>
                      )}
                  </>
                )}
              </FixaTabPanel>,
            ]}
          />
        </FixaModalBody>
      </FixaSheets>
    </FixaModal>
  );
};
